import React, { useState } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  ArrowUpDown, 
  Check, 
  Loader2
} from 'lucide-react';

const TailwindDataTable = ({
  data,
  isLoading,
  columns,
  page,
  setPage,
  pageSize,
  setPageSize,
  setSelectedRows,
  getRowId,
  ...restProps
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  // Total de páginas
  const totalPages = Math.ceil((data?.length || 0) / pageSize);
  
  // Função para alternar a seleção de uma linha
  const toggleRowSelection = (id) => {
    if (selectedIds.includes(id)) {
      const newSelectedIds = selectedIds.filter(selectedId => selectedId !== id);
      setSelectedIds(newSelectedIds);
      if (setSelectedRows) {
        setSelectedRows(newSelectedIds);
      }
    } else {
      const newSelectedIds = [...selectedIds, id];
      setSelectedIds(newSelectedIds);
      if (setSelectedRows) {
        setSelectedRows(newSelectedIds);
      }
    }
  };

  // Função para alternar a seleção de todas as linhas
  const toggleSelectAll = () => {
    if (selectedIds.length === data.length) {
      setSelectedIds([]);
      if (setSelectedRows) {
        setSelectedRows([]);
      }
    } else {
      const allIds = data.map(row => getRowId(row));
      setSelectedIds(allIds);
      if (setSelectedRows) {
        setSelectedRows(allIds);
      }
    }
  };

  // Função para determinar a classe de estilo da linha
  const getRowClassName = (row) => {
    if (row.is_removed) {
      return 'bg-red-100 dark:bg-red-900/30';
    }
    if (row.is_new) {
      return 'bg-yellow-100 dark:bg-yellow-900/30';
    }
    if (row.is_other) {
      return 'bg-blue-100 dark:bg-blue-900/30';
    }
    return '';
  };

  // Funções para ordenação (opcional)
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!data || !sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  // Dados paginados
  const paginatedData = sortedData?.slice(page * pageSize, (page + 1) * pageSize) || [];
  
  return (
    <div className="w-full overflow-hidden border rounded-lg bg-card shadow-sm">
      <div className="relative w-full overflow-auto" style={{ maxHeight: "80vh" }}>
        <table className="w-full caption-bottom text-sm">
          <thead className="bg-muted sticky top-0 z-10">
            <tr className="border-b">
              {/* Checkbox para seleção de todas as linhas */}
              <th className="h-12 w-12 px-4 text-left align-middle font-medium text-muted-foreground">
                <div className="flex h-12 items-center">
                  <button
                    onClick={toggleSelectAll}
                    className={`h-4 w-4 rounded border ${
                      selectedIds.length === data?.length ? 'bg-primary border-primary' : 'border-input'
                    } flex items-center justify-center`}
                  >
                    {selectedIds.length === data?.length && (
                      <Check className="h-3 w-3 text-primary-foreground" />
                    )}
                  </button>
                </div>
              </th>
              
              {/* Cabeçalhos de coluna */}
              {columns.map((column) => (
                <th 
                  key={column.field} 
                  className="h-12 px-4 text-left align-middle font-medium text-muted-foreground"
                  style={{ minWidth: column.minWidth, width: column.width }}
                >
                  {column.renderHeader ? (
                    column.renderHeader({ colDef: column })
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span>{column.headerName}</span>
                      <button 
                        onClick={() => requestSort(column.field)}
                        className="hover:bg-accent rounded-md p-1"
                      >
                        <ArrowUpDown className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody className="[&_tr:last-child]:border-0">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length + 1} className="h-32 text-center">
                  <div className="flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    <span className="ml-2">Carregando dados...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="h-32 text-center text-muted-foreground">
                  Nenhum registro encontrado
                </td>
              </tr>
            ) : (
              paginatedData.map((row) => {
                const rowId = getRowId(row);
                return (
                  <tr 
                    key={rowId} 
                    className={`border-b hover:bg-muted/50 ${getRowClassName(row)}`}
                  >
                    {/* Checkbox de seleção de linha */}
                    <td className="p-4 align-middle">
                      <button
                        onClick={() => toggleRowSelection(rowId)}
                        className={`h-4 w-4 rounded border ${
                          selectedIds.includes(rowId) ? 'bg-primary border-primary' : 'border-input'
                        } flex items-center justify-center`}
                      >
                        {selectedIds.includes(rowId) && (
                          <Check className="h-3 w-3 text-primary-foreground" />
                        )}
                      </button>
                    </td>
                    
                    {/* Células de dados */}
                    {columns.map((column) => (
                      <td key={`${rowId}-${column.field}`} className="p-4 align-middle">
                        {column.renderCell ? (
                          column.renderCell({
                            value: row[column.field],
                            row,
                            field: column.field,
                          })
                        ) : (
                          row[column.field]
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      {/* Paginação */}
      <div className="flex items-center justify-between px-4 py-4 border-t">
        <div className="flex-1 text-sm text-muted-foreground">
          {data?.length ? `Mostrando ${page * pageSize + 1}-${Math.min((page + 1) * pageSize, data.length)} de ${data.length} registros` : 'Nenhum registro'}
        </div>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Linhas por página</p>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              className="inline-flex h-9 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {[10, 20, 50, 100, 200, 500].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(0)}
              disabled={page === 0}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background p-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background p-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm">{`Página ${page + 1} de ${Math.max(1, totalPages)}`}</span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background p-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background p-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TailwindDataTable;